import zionDebug from '@fs/zion-debug'
import { getNextUuid } from '../../../../shared/utils/utils'

const debug = zionDebug('rip:FamilyReviewCorrection:reducer')

export const stepsMap = {
  STEP_ONE: '1',
  STEP_TWO: '2',
  STEP_THREE: '3',
  STEP_FOUR: '4',
}

export const defaultInitialState = {
  activeFieldId: '', // this is used when creating rectangles to set the field correctly on the highlight
  addNewEvent: {
    eventId: '',
    typeId: '',
  },
  addedSexId: '', // This is used when adding a sex in the add person flow
  currentStep: stepsMap.STEP_ONE,
  creatingRect: false, // whether or not the image viewer is in drawMode
  isAddingHighlight: false, // Whether or not the user is currently adding a highlight rather than editing existing ones
  selectedPersonId: '', // Represents the person with focus. This can be the primary person in step 1 or the focused card in step 2, or the person being edited in step 2
  selectedEventId: '',
  error: false,
  userId: '', // This id is used when saving
  statsTrackingUserAction: 'reviewed',
  stepTwo: {
    relationshipCards: [
      {
        personId: '',
        hasBeenReviewed: false,
      },
    ], // Represent persons in the record, and the reviewed status of each. Should be reset if step 1 is changed
    allReviewsComplete: false,
    initialized: false,
  },
  stepThree: {
    eventCards: [
      // {
      //   eventId: '',
      //   hasBeenReviewed: false,
      // },
    ], // Represent events and the reviewed status of each. Should be reset if step 1 is changed
    allReviewsComplete: false,
    initialized: false,
    addedFloatingEventPersonId: '',
  },
  splitScreenFocusedIndex: undefined,
  splitScreenPartitionKey: undefined,
  showImageGrid: false,
}

const reducer = (state, action) => {
  debug('reducer called: ', { state, type: action.type, value: action.value })

  switch (action.type) {
    case 'SET_STATE': // generic set state action
      return { ...state, ...action.value }
    case 'SET_STATS_TRACKING_USER_ACTION': {
      if (action.value !== 'fixed' && action.value !== 'reviewed')
        throw new Error('Invalid value for stats tracking. Valid values are "fixed" or "reviewed"')
      return {
        ...state,
        statsTrackingUserAction: action.value,
      }
    }
    case 'SET_SPLIT_SCREEN_FOCUSED_INDEX': {
      return {
        ...state,
        splitScreenFocusedIndex: action.value,
      }
    }
    case 'SET_SPLIT_SCREEN_PARTITION_KEY': {
      return {
        ...state,
        splitScreenPartitionKey: action.value,
      }
    }
    case 'SET_SHOW_IMAGE_GRID': {
      return {
        ...state,
        showImageGrid: action.value,
      }
    }
    case 'SET_ACTIVE_FIELD_ID': {
      return {
        ...state,
        activeFieldId: action.value,
      }
    }
    case 'SET_IS_ADDING_HIGHLIGHT': {
      return {
        ...state,
        isAddingHighlight: action.value,
      }
    }
    case 'SET_ADDED_FLOATING_EVENT_PERSON': {
      return {
        ...state,
        stepThree: {
          ...state.stepThree,
          addedFloatingEventPersonId: action.value,
        },
      }
    }
    case 'SET_ADD_NEW_EVENT_IDS': {
      const eventId = getNextUuid()
      return {
        ...state,
        addNewEvent: {
          eventId,
          typeId: getNextUuid(),
        },
        selectedEventId: eventId,
      }
    }
    case 'RESET_ADD_NEW_EVENT_IDS': {
      return {
        ...state,
        addNewEvent: {
          eventId: '',
          typeId: '',
        },
      }
    }
    case 'SET_CURRENT_STEP': {
      return {
        ...state,
        currentStep: action.value,
      }
    }
    case 'SET_CREATING_RECT': {
      return {
        ...state,
        creatingRect: action.value,
      }
    }
    case 'SET_ERROR':
      return {
        ...state,
        error: action.value,
      }
    case 'SET_SELECTED_PERSON_ID':
      return {
        ...state,
        selectedPersonId: action.value,
      }
    case 'SET_SELECTED_EVENT_ID':
      return {
        ...state,
        selectedEventId: action.value,
      }
    case 'SET_USER_ID':
      return { ...state, userId: action.value }
    case 'RESET_STEP_TWO': {
      return {
        ...state,
        stepTwo: {
          relationshipCards: [
            {
              personId: '',
              hasBeenReviewed: false,
            },
          ],
          allReviewsComplete: false,
          initialized: false,
        },
      }
    }
    case 'SET_STEP_TWO_CARD_REVIEWS': {
      const updatedCards = action.value.map((newCard) => {
        const existingCard = state.stepTwo.relationshipCards.find((card) => card?.personId === newCard?.personId)
        return {
          ...newCard,
          hasBeenReviewed: existingCard ? existingCard.hasBeenReviewed : false,
        }
      })

      return {
        ...state,
        stepTwo: {
          ...state.stepTwo,
          relationshipCards: updatedCards,
          allReviewsComplete: updatedCards.every((card) => card.hasBeenReviewed),
        },
        selectedPersonId: updatedCards.find((card) => !card.hasBeenReviewed)?.personId || updatedCards[0]?.personId,
      }
    }

    case 'SET_STEP_TWO_DATA': {
      let allReviewsComplete = false
      if (action?.value?.relationshipCards)
        allReviewsComplete = action.value.relationshipCards.every((cardState) => cardState.hasBeenReviewed)
      if (!state.stepTwo.initialized) {
        return {
          ...state,
          stepTwo: {
            ...state.stepTwo,
            relationshipCards: action.value.relationshipCards,
            initialized: true,
            allReviewsComplete,
          },
          selectedPersonId: action.value.selectedPersonId,
        }
      }
      const firstUnreviewedCardIndex = state.stepTwo.relationshipCards.findIndex(
        (cardState) => !cardState.hasBeenReviewed
      )
      const selectedPersonId =
        firstUnreviewedCardIndex !== -1 ? state.stepTwo.relationshipCards[firstUnreviewedCardIndex].personId : ''
      return {
        ...state,
        selectedPersonId,
      }
    }
    case 'RESET_STEP_THREE': {
      return {
        ...state,
        stepThree: {
          eventCards: [],
          allReviewsComplete: false,
          initialized: false,
        },
      }
    }
    case 'RESET_STEP_THREE_CARD_REVIEWS': {
      return {
        ...state,
        stepThree: {
          ...state.stepThree,
          eventCards: action.value,
          allReviewsComplete: false,
        },
        selectedEventId: action.value[0]?.eventId || state.stepThree.eventCards[0]?.eventId,
      }
    }
    case 'SET_STEP_THREE_DATA': {
      let allReviewsComplete = false
      if (action?.value?.eventCards)
        allReviewsComplete = action.value.eventCards.every((cardState) => cardState.hasBeenReviewed)
      if (!state.stepThree.initialized) {
        return {
          ...state,
          stepThree: {
            ...state.stepThree,
            eventCards: action.value.eventCards,
            initialized: true,
            allReviewsComplete,
          },
          selectedEventId: action.value.selectedEventId,
        }
      }
      const firstUnreviewedCardIndex = state.stepThree.eventCards.findIndex((cardState) => !cardState.hasBeenReviewed)
      const selectedEventId =
        firstUnreviewedCardIndex !== -1 ? state.stepThree.eventCards[firstUnreviewedCardIndex].eventId : ''
      return {
        ...state,
        selectedEventId,
      }
    }
    case 'MARK_RELATIONSHIP_CARD_CORRECT': {
      const newRelationshipCards = state.stepTwo.relationshipCards.map((cardState) => {
        return cardState.personId === action.value ? { ...cardState, hasBeenReviewed: true } : cardState
      })

      const unreviewedCardIndex = newRelationshipCards.findIndex((cardState) => !cardState.hasBeenReviewed)

      return {
        ...state,
        stepTwo: {
          ...state.stepTwo,
          relationshipCards: newRelationshipCards,
          allReviewsComplete: unreviewedCardIndex === -1,
        },
        selectedPersonId:
          unreviewedCardIndex !== -1 ? newRelationshipCards[unreviewedCardIndex].personId : state.selectedPersonId,
      }
    }
    case 'MARK_EVENT_CARD_CORRECT': {
      const newEventCards = state.stepThree.eventCards.map((cardState) => {
        return cardState.eventId === action.value ? { ...cardState, hasBeenReviewed: true } : cardState
      })

      const unreviewedCardIndex = newEventCards.findIndex((cardState) => !cardState.hasBeenReviewed)

      return {
        ...state,
        stepThree: {
          ...state.stepThree,
          eventCards: newEventCards,
          allReviewsComplete: unreviewedCardIndex === -1,
        },
        selectedEventId:
          unreviewedCardIndex !== -1 ? newEventCards[unreviewedCardIndex].eventId : state.selectedEventId,
      }
    }
    default: {
      throw new Error(`Unhandled type in Family Review reducer: ${action.type}`)
    }
  }
}

export default reducer
